// Fontes do painel inicial, ligadas à autorização do escopo ativo. Uma única chamada por tela: a fila de atenção, os
// cartões de módulo e os indicadores leem do mesmo cache (TanStack Query).
import type { Permission } from '../lib/permissions';
import { useInboxCounters } from '../modules/workflow/api/queries';
import {
  useBreakGlassPending,
  useDelegationsAwaitingMe,
  useHomeInbox,
  useMyAccessReviews,
  useStudiesOverview,
  useTenantsOverviewHome,
  useTwoKeyAwaitingMe,
} from './api';

export type HomeScope = 'network' | 'tenant';

export interface HomeAccess {
  readonly studies: boolean;
  readonly breakGlass: boolean;
  readonly tenants: boolean;
  readonly accessReviews: boolean;
  readonly twoKey: boolean;
  readonly delegations: boolean;
}

const any = (permissions: ReadonlySet<Permission>, list: readonly Permission[]): boolean =>
  list.some((permission) => permissions.has(permission));

/** O que o usuário pode ver no escopo; o servidor continua sendo a autoridade (um 403 vira "indisponível"). */
export function homeAccess(scope: HomeScope, permissions: ReadonlySet<Permission>): HomeAccess {
  const network = scope === 'network';
  return {
    studies: network && permissions.has('study.network.read'),
    breakGlass: network && permissions.has('breakglass.review'),
    tenants: network && permissions.has('tenant.read.network'),
    accessReviews: permissions.has('access.review.decide'),
    twoKey: any(permissions, ['authz.matrix.publish']),
    delegations:
      !network && any(permissions, ['delegation.read', 'delegation.manage', 'delegation.sign']),
  };
}

export function useHomeSignals(scope: HomeScope, permissions: ReadonlySet<Permission>) {
  const access = homeAccess(scope, permissions);
  return {
    access,
    inbox: useHomeInbox(),
    counters: useInboxCounters(),
    studies: useStudiesOverview(access.studies),
    breakGlass: useBreakGlassPending(access.breakGlass),
    tenants: useTenantsOverviewHome(access.tenants),
    accessReviews: useMyAccessReviews(access.accessReviews),
    twoKey: useTwoKeyAwaitingMe(access.twoKey),
    delegations: useDelegationsAwaitingMe(access.delegations),
  };
}

export type HomeSignals = ReturnType<typeof useHomeSignals>;
